import { v4 as uuid } from 'uuid';

export const SessionId = uuid();

const LOCALDEV = 'localdev';
const DEV = 'development';
const PROD = 'production';
const UAT = 'uat';
const NUTRIEN_ENV = import.meta.env.NUTRIEN_ENV || DEV;

console.log('NUTRIEN_ENV:', NUTRIEN_ENV);

const baseAppConfig = (env) => {
  const defaultConfig = {
    /* Enable same sign on by automatically redirecting the application to the SAML SSO provider */
    automaticFederatedSigninRedirectEnabled: false,
    /* log level */
    logLevel: (typeof window !== 'undefined' && window.LOG_LEVEL) || 'INFO',
    /* kinesis stream name to stream logs to */
    kinesisStreamName: '',
  };

  const devConfig = {
    ...defaultConfig,
    logLevel: 'INFO',
    kinesisStreamName: 'epermitting-development-ui-logstream',
  };

  return {
    [LOCALDEV]: devConfig,
    [DEV]: devConfig,
    [UAT]: {
      ...defaultConfig,
      logLevel: 'INFO',
      kinesisStreamName: 'epermitting-uat-ui-logstream',
    },
    [PROD]: {
      ...defaultConfig,
      logLevel: 'INFO',
      kinesisStreamName: 'epermitting-production-ui-logstream',
    },
  }[env];
};

const apiConfig = (env) => {
  const defaultConfig = {
    /* Base url for API calls */
    baseUrl: '',
    /* Amount of time between when the app checks for permit updates */
    throttleLength: 60000 * 15, // 15 minutes
  };

  const devConfig = {
    ...defaultConfig,
    baseUrl: 'https://work-permit-api-dev.nutrien.com/',
    throttleLength: 900,
  };

  return {
    [LOCALDEV]: devConfig,
    [DEV]: devConfig,
    [UAT]: {
      ...defaultConfig,
      baseUrl: 'https://work-permit-api-uat.nutrien.com/',
      throttleLength: 900,
    },
    [PROD]: {
      ...defaultConfig,
      baseUrl: 'https://work-permit-api.nutrien.com/',
      throttleLength: 900,
    },
  }[env];
};

const formIOConfig = (env) => {
  const devConfig = {
    serverUrl: 'http://form-io-dev.np.nutrien.com',
  };

  return {
    [LOCALDEV]: devConfig,
    [DEV]: devConfig,
    [UAT]: {
      serverUrl: 'http://form-io.nutrien.com',
    },
    [PROD]: {
      serverUrl: 'http://form-io.nutrien.com',
    },
  }[env];
};

const amplifyAuthConfig = (env) => {
  const defaultConfig = {
    /* Cognito federated IdP alias used for ADFS SSO */
    adfsFederatedIdentityProviderId: 'nutrien-e-permitting-ad',
    /* AWS region where Cognito resources are hosted */
    region: 'us-east-2',
    /* Cognito app client id used by Amplify Auth */
    userPoolWebClientId: '1brehhom2juekumfnikq74nsr5',
    /* Cognito user pool id for authenticating users */
    userPoolId: 'us-east-2_0lVhYTSgR',
    /* Cognito identity pool id for AWS credential federation */
    identityPoolId: 'us-east-2:db449005-3f86-467f-9c13-5e7f5881aa53',
    oauth: {
      /* Cognito hosted UI domain */
      domain: 'work-permit-dev.auth.us-east-2.amazoncognito.com',
      /* OAuth scopes requested during sign-in */
      scope: ['email', 'profile', 'openid'],
      /* Callback URL after successful sign-in */
      redirectSignIn: 'http://localhost:5000/',
      /* Callback URL after sign-out */
      redirectSignOut: 'http://localhost:5000/auth/signout',
      /* OAuth grant response type for Cognito hosted UI */
      responseType: 'code',
    },
  };

  return {
    [LOCALDEV]: defaultConfig,
    [DEV]: {
      ...defaultConfig,
      userPoolWebClientId: '2v90vdntbl930gu90v0k8637ka',
      userPoolId: 'us-east-2_0lVhYTSgR',
      identityPoolId: 'us-east-2:db449005-3f86-467f-9c13-5e7f5881aa53',
      oauth: {
        ...defaultConfig.oauth,
        domain: 'work-permit-dev.auth.us-east-2.amazoncognito.com',
        redirectSignIn: 'https://work-permit-dev.nutrien.com',
        redirectSignOut: 'https://work-permit-dev.nutrien.com/auth/signout',
      },
    },
    [UAT]: {
      ...defaultConfig,
      userPoolWebClientId: '7vaokc6ea2fe7r2l6hpir72n1m',
      userPoolId: 'us-east-2_dYPGusGsx',
      identityPoolId: 'us-east-2:ba60c10c-3393-40c7-8ffe-022b793b899a',
      oauth: {
        ...defaultConfig.oauth,
        domain: 'work-permit-uat.auth.us-east-2.amazoncognito.com',
        redirectSignIn: 'https://work-permit-uat.nutrien.com/',
        redirectSignOut: 'https://work-permit-uat.nutrien.com/auth/signout',
      },
    },
    [PROD]: {
      ...defaultConfig,
      userPoolWebClientId: '6n2qthcd1n6jo73qmti6qrfsdn',
      userPoolId: 'us-east-2_LCEd2s3sB',
      identityPoolId: 'us-east-2:d85335e3-8110-43f3-94a0-6a3c6d52d77a',
      oauth: {
        ...defaultConfig.oauth,
        domain: 'work-permit.auth.us-east-2.amazoncognito.com',
        redirectSignIn: 'https://work-permit.nutrien.com',
        redirectSignOut: 'https://work-permit.nutrien.com/auth/signout',
      },
    },
  }[env];
};

const awss3Config = (env) => {
  const defaultConfig = {
    /* AWS region for S3 uploads/downloads */
    region: 'us-east-2',
  };

  const devConfig = {
    ...defaultConfig,
    /* S3 bucket for development app data */
    bucket: 'eaoperations-epermitting-app-data-dev',
    /* Cognito identity pool used to authorize S3 access */
    identityPoolId: 'us-east-2:db449005-3f86-467f-9c13-5e7f5881aa53',
  };

  return {
    [LOCALDEV]: devConfig,
    [DEV]: devConfig,
    [UAT]: {
      ...defaultConfig,
      bucket: 'eaoperations-epermitting-app-data-uat',
      identityPoolId: 'us-east-2:db449005-3f86-467f-9c13-5e7f5881aa53',
    },
    [PROD]: {
      ...defaultConfig,
      bucket: 'eaoperations-epermitting-app-data-prod',
      identityPoolId: 'us-east-2:db449005-3f86-467f-9c13-5e7f5881aa53',
    },
  }[env];
};

const cacheConfig = () => {
  return {
    /* Amount of time between when the app checks for permit updates */
    expirePermitsAfter: 60000 * 15, // 15 minutes
    /* Amount of time between when the app refreshes the location metadata */
    expireLocationMetadataAfter: 60000 * 5, // 5 minutes
  };
};

const permitsConfig = () => {
  return {
    /* Amount of time between saving a permit draft */
    saveDraftInterval: 20000, // 20 seconds
  };
};

const awsKinesisConfig = () => {
  return {
    /* AWS region where the Kinesis stream is provisioned */
    region: 'us-east-2',
    /* Number of retry records buffered before attempting send */
    bufferSize: 10,
    /* Max number of records per Kinesis PutRecords request */
    flushSize: 100,
    /* Milliseconds between automatic buffer flush attempts */
    flushInterval: 5000,
    /* Number of resend attempts after a failed publish */
    resendLimit: 5,
  };
};

const AppConfig = {
  App: baseAppConfig(NUTRIEN_ENV),
  API: apiConfig(NUTRIEN_ENV),
  Cache: cacheConfig(),
  Permits: permitsConfig(),
  FormIO: formIOConfig(NUTRIEN_ENV),
  Amplify: {
    Auth: amplifyAuthConfig(NUTRIEN_ENV),
    Storage: {
      AWSS3: awss3Config(NUTRIEN_ENV),
    },
    Analytics: {
      AWSKinesis: awsKinesisConfig(),
    },
  },
};

export default AppConfig;
